import React from 'react';
import { SavedChat } from '../types';
import { Button } from './Button';
import { AdBanner } from './AdBanner';

interface HomeStepProps {
  chats: SavedChat[];
  onNewChat: () => void;
  onOpenChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
}

export const HomeStep: React.FC<HomeStepProps> = ({ chats, onNewChat, onOpenChat, onDeleteChat }) => {
  // Sort chats by last updated (newest first)
  const sortedChats = [...chats].sort((a, b) => b.lastUpdated - a.lastUpdated);

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-slate-900">Your Conversations</h1>
          <p className="text-slate-500 text-sm">Review past coaching sessions or start fresh.</p>
        </div>

        <Button onClick={onNewChat} fullWidth className="shadow-brand-500/25">
          <span className="text-lg mr-1">+</span> Start New Practice Session
        </Button>

        <div className="space-y-4">
          {sortedChats.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-2xl border border-slate-100 shadow-sm">
              <div className="text-4xl mb-3">👋</div>
              <h3 className="text-lg font-medium text-slate-900">No chats yet</h3>
              <p className="text-slate-400 text-sm px-6">Start a new conversation to begin improving your communication skills.</p>
            </div>
          ) : (
            sortedChats.map(chat => (
              <button
                key={chat.id}
                onClick={() => onOpenChat(chat.id)}
                className="w-full text-left bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-brand-200 transition-all group relative overflow-hidden"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 mr-4">
                    <h3 className="font-semibold text-slate-900 line-clamp-1 group-hover:text-brand-600 transition-colors">
                      {chat.scenario.relationshipStatus}
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(chat.lastUpdated).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} • {chat.scenario.difficulty}
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <div className={`px-2 py-1 rounded-lg text-xs font-bold border ${
                        chat.averageScore >= 7 ? 'bg-green-50 text-green-700 border-green-100' :
                        chat.averageScore >= 4 ? 'bg-amber-50 text-amber-700 border-amber-100' :
                        'bg-red-50 text-red-700 border-red-100'
                    }`}>
                        {chat.averageScore > 0 ? chat.averageScore.toFixed(1) : '-'} / 10
                    </div>
                    
                    <div 
                        onClick={(e) => {
                            e.stopPropagation();
                            onDeleteChat(chat.id);
                        }}
                        className="p-1 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors z-10"
                        title="Delete conversation"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-slate-600 line-clamp-2 bg-slate-50 p-2 rounded-lg border border-slate-100">
                  <span className="font-medium text-slate-400 text-xs uppercase tracking-wider block mb-1">Scenario</span>
                  {chat.scenario.context}
                </p>
              </button>
            ))
          )}
        </div>
        
        {/* Extra padding for bottom ad */}
        <div className="h-12"></div>
      </div>
      
      <div className="mt-auto bg-white border-t border-slate-100 z-10 pb-safe">
        <AdBanner position="bottom" />
      </div>
    </div>
  );
};